import {FC, useState, useEffect, useCallback, useRef} from 'react'
import {createPortal} from 'react-dom'
import axios from 'axios'
import {useAuth} from '../../../../app/modules/auth/core/Auth'
import '../../../../app/modules/admin/issues/issues.css'
import {AccountIssue, AdminOption, ISSUE_DEPARTMENTS} from '../../../../app/modules/admin/issues/core/_models'
import {StatusPill, WaitingPill, AttachmentList, MediaDropzone, timeAgo, fullDate} from '../../../../app/modules/admin/issues/core/ui'

const API_URL = process.env.REACT_APP_API_URL || ''

type Attachment = {url: string; name: string; type: string}

// A stale/expired admin_auth_token can linger in a client's browser (e.g. shared
// machine), so presence alone isn't enough — decode the JWT and require an admin
// role that hasn't expired. Otherwise the widget must stay hidden for clients.
const isValidAdminToken = (token: string | null): boolean => {
  if (!token) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (payload?.role !== 'admin') return false
    if (payload?.exp && payload.exp * 1000 <= Date.now()) return false
    return true
  } catch {
    return false
  }
}

// Floating issues widget shown ONLY when an admin is logged in. A global
// quick-view of all issues across accounts: each shows when it was opened and a
// "login as" link to each affected account. New issues are created via
// /admin/issues (admin token); media uploads to /admin/issues/upload first.
const AdminIssuesWidget: FC = () => {
  const {currentUser} = useAuth()
  // Only a valid, unexpired admin JWT counts — a lingering token must not reveal
  // the widget to a client. Nulling it here makes every `!adminToken` guard below
  // (render + fetch) treat an invalid session as "not admin".
  const adminToken = useRef<string | null>(
    (() => {
      const raw = localStorage.getItem('admin_auth_token')
      return isValidAdminToken(raw) ? raw : null
    })()
  ).current
  // The account currently being viewed (admin impersonating a client) — the
  // issue is opened "from" this profile, so auto-attach it to the checklist.
  const viewedAccountId = (currentUser as any)?._id || (currentUser as any)?.id
  const [open, setOpen] = useState(false)
  const [issues, setIssues] = useState<AccountIssue[]>([])
  const [openCount, setOpenCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const [showAdd, setShowAdd] = useState(false)
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [department, setDepartment] = useState('')
  const [assignee, setAssignee] = useState('')
  const [admins, setAdmins] = useState<AdminOption[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const authHeader = {Authorization: `Bearer ${adminToken}`}

  const load = useCallback(async () => {
    if (!adminToken) return
    setLoading(true)
    try {
      const res = await axios.get(`${API_URL}/admin/issues`, {headers: authHeader, withCredentials: true})
      setIssues(res.data.issues || [])
      setOpenCount(res.data.open_count || 0)
    } catch {
      setIssues([])
      setOpenCount(0)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken])

  useEffect(() => {
    if (adminToken) load()
  }, [adminToken, load])

  // Load admins (for the assignee dropdown) once, when the add-form is first opened.
  useEffect(() => {
    if (!showAdd || !adminToken || admins.length) return
    axios
      .get(`${API_URL}/admin/issues/admins`, {headers: authHeader, withCredentials: true})
      .then((res) => setAdmins(res.data.admins || []))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdd, adminToken])

  if (!adminToken) return null

  const onPickFiles = async (files: FileList | null) => {
    if (!files || !files.length) return
    setUploading(true)
    try {
      const uploaded: Attachment[] = []
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData()
        fd.append('file', files[i])
        const res = await axios.post(`${API_URL}/admin/issues/upload`, fd, {
          headers: {...authHeader, 'Content-Type': 'multipart/form-data'},
          withCredentials: true,
        })
        uploaded.push(res.data)
      }
      setAttachments((prev) => [...prev, ...uploaded])
    } finally {
      setUploading(false)
    }
  }

  const submit = async () => {
    if (!title.trim()) return
    setSaving(true)
    try {
      await axios.post(
        `${API_URL}/admin/issues`,
        {
          title: title.trim(),
          description: desc.trim(),
          department: department || undefined,
          assigned_to: assignee || undefined,
          attachments,
          account_id: viewedAccountId, // auto-assign the profile it was opened from
          created_by: 'admin',
        },
        {headers: authHeader, withCredentials: true}
      )
      setTitle('')
      setDesc('')
      setDepartment('')
      setAssignee('')
      setAttachments([])
      setShowAdd(false)
      await load()
    } finally {
      setSaving(false)
    }
  }

  const loginAsAccount = async (accountId: string) => {
    try {
      const res = await axios.get(`${API_URL}/admin/switch-account/${accountId}`, {
        headers: authHeader,
        withCredentials: true,
      })
      if (res.data.success && res.data.token) {
        window.location.href = '/auth/auto-login?token=' + encodeURIComponent(res.data.token)
      } else {
        alert('לא ניתן להתחבר לחשבון')
      }
    } catch {
      alert('שגיאה בהתחברות לחשבון')
    }
  }

  const openIssues = issues.filter((i) => i.status !== 'done')
  const closedIssues = issues.filter((i) => i.status === 'done')

  const renderIssue = (i: AccountIssue) => (
    <div
      key={i._id}
      className='iss-card'
      style={{padding: 14, marginBottom: 10, opacity: i.status === 'done' ? 0.72 : 1}}
    >
      <div className='d-flex align-items-center justify-content-between mb-1' style={{gap: 8}}>
        <span className='fw-bold text-truncate' style={{fontSize: 14, color: '#1b1f2a'}}>
          {i.title || '(ללא כותרת)'}
        </span>
        <StatusPill status={i.status} />
      </div>

      <div className='d-flex align-items-center gap-2 mb-2' style={{fontSize: 11, color: '#8a92a6'}}>
        <span title={fullDate(i.created_at)}>
          <i className='bi bi-clock me-1'></i>
          {timeAgo(i.created_at)}
        </span>
        <WaitingPill waiting={i.waiting} />
      </div>

      {i.description && (
        <div style={{fontSize: 13, color: '#3a4151', lineHeight: 1.5, marginBottom: 8}}>{i.description}</div>
      )}

      {i.attachments && i.attachments.length > 0 && (
        <div className='mb-2'>
          <AttachmentList attachments={i.attachments} size={46} />
        </div>
      )}

      {i.accounts && i.accounts.length > 0 && (
        <div className='d-flex flex-wrap gap-2'>
          {i.accounts.map((a) => (
            <span key={a.account} className='d-inline-flex align-items-center gap-1'>
              <button
                className='iss-btn iss-btn-ghost iss-btn-sm'
                onClick={() => loginAsAccount(a.account)}
                title='התחבר לחשבון זה'
              >
                <i className='bi bi-box-arrow-in-left'></i>
                {a.name || a.account}
              </button>
              {a.phone && (
                <a
                  href={`tel:${a.phone}`}
                  className='iss-chip'
                  style={{background: '#eef0f5', color: '#4a5163', textDecoration: 'none'}}
                  title='התקשר ללקוח'
                >
                  <i className='bi bi-telephone'></i>
                  {a.phone}
                </a>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      <div className='app-navbar-item ms-2 d-flex align-items-center gap-2'>
        {openCount > 0 && (
          <a
            href='/admin/issues'
            className='d-flex align-items-center text-white fw-bold'
            style={{
              background: '#f1416c',
              borderRadius: 999,
              padding: '5px 11px',
              fontSize: 12,
              textDecoration: 'none',
              boxShadow: '0 2px 8px rgba(241,65,108,.35)',
            }}
            title='כל התקלות הפתוחות'
          >
            <i className='bi bi-exclamation-triangle me-1'></i>
            {openCount}
          </a>
        )}
        <button
          className='btn btn-sm btn-icon btn-light-warning'
          onClick={() => {
            setOpen(true)
            load()
          }}
          title='תקלות'
        >
          <i className='bi bi-exclamation-triangle fs-4'></i>
        </button>
      </div>

      {open &&
        createPortal(
          <div
            className='iss-scope'
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15,23,42,0.45)',
              backdropFilter: 'blur(3px)',
              zIndex: 1090,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              padding: '6vh 16px',
            }}
          >
            <div
              dir='rtl'
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(580px, 100%)',
                maxHeight: '86vh',
                display: 'flex',
                flexDirection: 'column',
                background: '#fff',
                borderRadius: 20,
                boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div
                className='d-flex align-items-center justify-content-between'
                style={{padding: '18px 22px', borderBottom: '1px solid #f1f2f5'}}
              >
                <div className='d-flex align-items-center gap-3'>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 13,
                      background: '#fff1f4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <i className='bi bi-exclamation-triangle' style={{color: '#f1416c', fontSize: 19}}></i>
                  </div>
                  <div>
                    <div style={{fontWeight: 800, fontSize: 18, color: '#1b1f2a'}}>תקלות</div>
                    <div style={{fontSize: 12, color: '#8a92a6'}}>{openCount} פתוחות</div>
                  </div>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <button className='iss-btn iss-btn-primary iss-btn-sm' onClick={() => setShowAdd(!showAdd)}>
                    <i className={`bi bi-${showAdd ? 'x-lg' : 'plus-lg'}`}></i>
                    {showAdd ? 'ביטול' : 'תקלה'}
                  </button>
                  <button className='iss-iconbtn' onClick={() => setOpen(false)}>
                    <i className='bi bi-x-lg'></i>
                  </button>
                </div>
              </div>

              {/* Body */}
              <div style={{padding: '18px 22px', overflowY: 'auto'}}>
                {showAdd && (
                  <div style={{background: '#f8f9fb', borderRadius: 14, padding: 16, marginBottom: 18}}>
                    <input
                      className='iss-input mb-2'
                      placeholder='כותרת התקלה'
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                    <textarea
                      className='iss-textarea mb-2'
                      placeholder='תיאור (אופציונלי)'
                      value={desc}
                      onChange={(e) => setDesc(e.target.value)}
                    />
                    <div className='d-flex gap-2 mb-2' style={{flexWrap: 'wrap'}}>
                      <select
                        className='iss-input'
                        style={{flex: 1, minWidth: 130}}
                        value={department}
                        onChange={(e) => {
                          setDepartment(e.target.value)
                          setAssignee('')
                        }}
                      >
                        <option value=''>מחלקה...</option>
                        {ISSUE_DEPARTMENTS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                      <select
                        className='iss-input'
                        style={{flex: 1, minWidth: 130}}
                        value={assignee}
                        onChange={(e) => setAssignee(e.target.value)}
                      >
                        <option value=''>שייך ל...</option>
                        {(department ? admins.filter((a) => a.departments?.includes(department)) : admins).map((a) => (
                          <option key={a._id} value={a._id}>
                            {a.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='mb-2'>
                      <MediaDropzone
                        attachments={attachments}
                        uploading={uploading}
                        onPick={(files) => onPickFiles(files)}
                        onRemove={(idx) => setAttachments((p) => p.filter((_, k) => k !== idx))}
                      />
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <button
                        className='iss-btn iss-btn-primary ms-auto'
                        onClick={submit}
                        disabled={saving || uploading || !title.trim()}
                      >
                        {saving ? 'שומר...' : 'דווח תקלה'}
                      </button>
                    </div>
                  </div>
                )}

                {loading ? (
                  <div className='iss-empty'>
                    <span className='spinner-border text-primary' role='status'></span>
                  </div>
                ) : (
                  <>
                    <div style={{fontSize: 11, fontWeight: 700, color: '#8a92a6', marginBottom: 8}}>
                      פתוחות ({openIssues.length})
                    </div>
                    {openIssues.length === 0 && (
                      <div style={{fontSize: 12, color: '#b3b9c7', marginBottom: 16}}>אין תקלות פתוחות</div>
                    )}
                    {openIssues.map(renderIssue)}

                    {closedIssues.length > 0 && (
                      <>
                        <div style={{fontSize: 11, fontWeight: 700, color: '#8a92a6', margin: '16px 0 8px'}}>
                          סגורות ({closedIssues.length})
                        </div>
                        {closedIssues.map(renderIssue)}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{padding: '14px 22px', borderTop: '1px solid #f1f2f5'}}>
                <a href='/admin/issues' className='iss-btn iss-btn-ghost w-100' style={{justifyContent: 'center'}}>
                  <i className='bi bi-box-arrow-up-left'></i>
                  צפה בכל התקלות במערכת
                </a>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default AdminIssuesWidget
